import { motion } from 'motion/react';
import React, { useEffect, useMemo, useState } from 'react';
import Aside from '../../../components/Aside';
import Button from '../../../components/Button';
import Checkbox from '../../../components/Checkbox';
import Icon from '../../../components/Icon';
import Loader from '../../../components/Loader';
import NotificationCounter from '../../../components/NotificationCounter';
import SideNavListSection from '../../../components/sideNav/SideNavListSection';
import SideNavFilter from '../../../components/sideNav/SideNavFilter';
import SideNavItem from '../../../components/sideNav/SideNavItem';
import SideNavText from '../../../components/sideNav/SideNavText';
import SideNavTitle from '../../../components/sideNav/SideNavTitle';
import SideNavVirtualListWrapper from '../../../components/sideNav/SideNavVirtualListWrapper';
import SlideButton from '../../../components/SlideButton';
import OrganisationUnitsText from '../../../components/OrganisationUnitsText';
import {
  getAllCategoriesService,
  queryAreasService,
  queryElementsService,
  queryRolesService,
} from '../../../services/contentService';
import {
  CHAMP_ID,
  DELETED_GROUP_ID,
  DELETED_PERSON_ID,
  EMPTY_ID,
  EXPERT_ID,
  KNOWLEDGE_OWNER_ID,
  MENTOR_ID,
  NONE_ID,
  SELF_STUDY_ID,
} from '../../../constants';
import {
  getAllOrganisationUnitsService,
  getOrganisationUnitRootNodesService,
} from '../../../services/organisationService';
import { getAllPersonsService } from '../../../services/personsService';
import localeLookup from '../../../config/locale';
import { searchString } from '../../../utils/helpers';
import ElementStatusElementConfigurationFilter from '../ElementStatusElementConfigurationFilter';
import ElementStatusV2FilterList from './ElementStatusV2FilterList';
import ElementStatusOrganisationUnitFilter from '../ElementStatusOrganisationUnitFilter';
import ElementStatusOtherFiltersFilter from '../ElementStatusOtherFiltersFilter';
import ElementStatusStatusFilter from '../ElementStatusStatusFilter';
import { filterDefaultState, isAnyFiltersActive } from './elementStatusV2Utils';

const AnimatedAside = motion(Aside);

const submenuVariants = {
  hidden: {
    x: '-100%',
    transition: {
      ease: 'easeIn',
    },
  },
  visible: {
    x: 0,
    transition: {
      ease: 'easeIn',
    },
  },
};

const getResponseData = (response) => response?.data || response || {};

export default function ElementStatusV2Filters({
  activeFilterId = null,
  allFilterIds = {},
  catalogAreas = {},
  catalogCategories = {},
  catalogElements = {},
  catalogOrganisationUnits = {},
  catalogPersons = {},
  catalogRoles = {},
  defaultFilters = {},
  defaultFilterId = null,
  filters = filterDefaultState,
  groups = {},
  organisationUnitRootNodes = {},
  organisationUnits = {},
  onApplySavedFilter = () => {},
  onFiltersChange = () => {},
  onCreateSavedFilter = () => {},
  onDeleteSavedFilter = () => {},
  onOverwriteSavedFilter = () => {},
  onOrganisationUnitToggle = () => {},
  onOrganisationUnitToggleMultiple = () => {},
  onResetFilters = () => {},
  onSetDefaultFilter = () => {},
  onSubmenuDeselectAll = () => {},
  onSubmenuItemToggle = () => {},
  onSubmenuSelectAll = () => {},
  onUpdateSavedFilter = () => {},
  presetFilters = {},
  submenuItems = {},
  wildcardPersons = {},
}) {
  const filterCategories = [
    {
      key: 'status',
      title: localeLookup('translations.Status'),
      type: 'settings',
    },
    {
      key: 'elementConfiguration',
      title: localeLookup('translations.Element configuration'),
      type: 'settings',
    },
    {
      key: 'organisationUnits',
      title: localeLookup('translations.Organisation units'),
      type: 'counter',
    },
    {
      key: 'roles',
      title: localeLookup('translations.Roles'),
      type: 'counter',
    },
    {
      key: 'knowledgeAreas',
      title: localeLookup('translations.Knowledge areas'),
      type: 'counter',
    },
    {
      key: 'knowledgeElements',
      title: localeLookup('translations.Elements'),
      type: 'counter',
    },
    {
      key: 'persons',
      title: localeLookup('translations.Persons'),
      type: 'counter',
    },
    {
      key: 'trainers',
      title: localeLookup('translations.Responsible'),
      type: 'counter',
    },
    {
      key: 'mentors',
      title: localeLookup('translations.Mentor'),
      type: 'counter',
    },
    {
      key: 'otherFilters',
      title: localeLookup('translations.Other filters'),
      type: 'settings',
    },
  ];
  const hasFilters =
    Object.keys(defaultFilters).length + Object.keys(presetFilters).length > 0;
  const [activeSubMenuKey, setActiveSubMenuKey] = useState(null);
  const [filterSearchQuery, setFilterSearchQuery] = useState('');
  const [loadedOptionsByFilter, setLoadedOptionsByFilter] = useState({});
  const [isLoadingOptionsByFilter, setIsLoadingOptionsByFilter] = useState({});
  const [loadedOrganisationUnits, setLoadedOrganisationUnits] = useState({
    organisationUnitRootNodes,
    organisationUnits,
  });
  const archivedPersonState = 'Archived';

  const handleSubmenuBack = () => {
    setActiveSubMenuKey(null);
    setFilterSearchQuery('');
  };

  const sortItemsByName = (items = []) =>
    [...items].sort((left, right) =>
      (left.name || '').localeCompare(right.name || '')
    );

  const getPersonLikeSubtitle = (item) =>
    item?.state === archivedPersonState || item?.isActive === false
      ? localeLookup('translations.Archived')
      : '';

  const getPersonLikeTooltip = (item) =>
    item?.initials
      ? `${item.initials}${
          item.employeeNumber ? ` · ${item.employeeNumber}` : ''
        }`
      : null;

  const peopleLikeCatalog = {
    ...groups,
    ...wildcardPersons,
    ...catalogPersons,
  };

  useEffect(() => {
    let isMounted = true;

    const loadOptionsForActiveFilter = async () => {
      if (!activeSubMenuKey || loadedOptionsByFilter[activeSubMenuKey]) {
        return;
      }

      if (submenuItems[activeSubMenuKey]?.length > 0) {
        setLoadedOptionsByFilter((current) => ({
          ...current,
          [activeSubMenuKey]: submenuItems[activeSubMenuKey],
        }));
        setIsLoadingOptionsByFilter((current) => ({
          ...current,
          [activeSubMenuKey]: false,
        }));
        return;
      }

      setIsLoadingOptionsByFilter((current) => ({
        ...current,
        [activeSubMenuKey]: true,
      }));

      if (activeSubMenuKey === 'organisationUnits') {
        if (
          Object.keys(organisationUnits || {}).length > 0 &&
          Object.keys(organisationUnitRootNodes || {}).length > 0
        ) {
          setLoadedOrganisationUnits({
            organisationUnitRootNodes,
            organisationUnits,
          });
          setLoadedOptionsByFilter((current) => ({
            ...current,
            organisationUnits: true,
          }));
          setIsLoadingOptionsByFilter((current) => ({
            ...current,
            organisationUnits: false,
          }));
          return;
        }

        const [organisationUnitsResponse, rootNodesResponse] =
          await Promise.all([
            getAllOrganisationUnitsService(),
            getOrganisationUnitRootNodesService('accessible'),
          ]);

        if (!isMounted) return;

        const organisationUnitsData = getResponseData(
          organisationUnitsResponse
        );
        const rootNodesData = getResponseData(rootNodesResponse);

        const rootNodes = Array.isArray(rootNodesData)
          ? rootNodesData.reduce((accumulator, unitId) => {
              accumulator[unitId] = organisationUnitsData[unitId] || {
                id: unitId,
              };
              return accumulator;
            }, {})
          : rootNodesData;

        setLoadedOrganisationUnits({
          organisationUnitRootNodes: rootNodes || {},
          organisationUnits: organisationUnitsData || {},
        });
        setLoadedOptionsByFilter((current) => ({
          ...current,
          organisationUnits: true,
        }));
        setIsLoadingOptionsByFilter((current) => ({
          ...current,
          organisationUnits: false,
        }));
        return;
      }

      if (activeSubMenuKey === 'roles') {
        const roles = await queryRolesService();
        if (isMounted) {
          setLoadedOptionsByFilter((current) => ({
            ...current,
            roles: getResponseData(roles),
          }));
          setIsLoadingOptionsByFilter((current) => ({
            ...current,
            roles: false,
          }));
        }
        return;
      }

      if (activeSubMenuKey === 'knowledgeAreas') {
        const [areas, categories] = await Promise.all([
          queryAreasService(),
          getAllCategoriesService(),
        ]);
        if (isMounted) {
          setLoadedOptionsByFilter((current) => ({
            ...current,
            knowledgeAreas: {
              areas: getResponseData(areas),
              categories: getResponseData(categories),
            },
          }));
          setIsLoadingOptionsByFilter((current) => ({
            ...current,
            knowledgeAreas: false,
          }));
        }
        return;
      }

      if (activeSubMenuKey === 'knowledgeElements') {
        const [elements, areas] = await Promise.all([
          queryElementsService(),
          queryAreasService(),
        ]);
        if (isMounted) {
          setLoadedOptionsByFilter((current) => ({
            ...current,
            knowledgeElements: {
              areas: getResponseData(areas),
              elements: getResponseData(elements),
            },
          }));
          setIsLoadingOptionsByFilter((current) => ({
            ...current,
            knowledgeElements: false,
          }));
        }
        return;
      }

      if (['persons', 'trainers', 'mentors'].includes(activeSubMenuKey)) {
        const persons = await getAllPersonsService();
        if (isMounted) {
          setLoadedOptionsByFilter((current) => ({
            ...current,
            [activeSubMenuKey]: getResponseData(persons),
          }));
          setIsLoadingOptionsByFilter((current) => ({
            ...current,
            [activeSubMenuKey]: false,
          }));
        }
      }
    };

    loadOptionsForActiveFilter();

    return () => {
      isMounted = false;
    };
  }, [activeSubMenuKey, loadedOptionsByFilter]);

  const getTrainerDefaultItems = (trainerItems = []) => {
    const wildcardNames = {
      [KNOWLEDGE_OWNER_ID]: localeLookup(
        'translations.Missing knowledge owner'
      ),
      [EXPERT_ID]: localeLookup('translations.Missing expert'),
      [MENTOR_ID]: localeLookup('translations.Missing mentor'),
    };

    const staticItems = [
      {
        id: EMPTY_ID,
        name: localeLookup('translations.No responsible'),
        selected: !filters.trainers.deselected.includes(EMPTY_ID),
      },
      {
        id: DELETED_PERSON_ID,
        name: localeLookup('translations.Deleted person'),
        selected: !filters.trainers.deselected.includes(DELETED_PERSON_ID),
      },
    ];

    const wildcardItems = trainerItems.filter((item) =>
      [KNOWLEDGE_OWNER_ID, EXPERT_ID, MENTOR_ID].includes(item.id)
    );

    return [
      ...staticItems,
      ...wildcardItems.map((item) => ({
        ...item,
        name: wildcardNames[item.id] || item.name,
      })),
    ];
  };

  const getMentorDefaultItems = () => [
    {
      id: EMPTY_ID,
      name: localeLookup('translations.(Empty) - Not in training'),
      selected: !filters.mentors.deselected.includes(EMPTY_ID),
    },
    {
      id: NONE_ID,
      name: localeLookup('translations.No mentor'),
      selected: !filters.mentors.deselected.includes(NONE_ID),
    },
    {
      id: DELETED_PERSON_ID,
      name: localeLookup('translations.Deleted person'),
      selected: !filters.mentors.deselected.includes(DELETED_PERSON_ID),
    },
    {
      id: DELETED_GROUP_ID,
      name: localeLookup('translations.Deleted group'),
      selected: !filters.mentors.deselected.includes(DELETED_GROUP_ID),
    },
  ];

  const derivedActiveSubmenuItems = useMemo(() => {
    if (activeSubMenuKey === 'roles') {
      if (Array.isArray(loadedOptionsByFilter.roles)) {
        return loadedOptionsByFilter.roles;
      }
      const roles =
        Object.keys(loadedOptionsByFilter.roles || {}).length > 0
          ? loadedOptionsByFilter.roles
          : catalogRoles;
      return sortItemsByName(
        Object.keys(roles).map((id) => ({
          id,
          name: roles[id]?.name || id,
          searchString: `${roles[id]?.name || ''}${(
            roles[id]?.organisationUnits || []
          )
            .map((unitId) => catalogOrganisationUnits[unitId]?.name || '')
            .join(' ')}`.toLowerCase(),
          selected: !filters.roles.deselected.includes(id),
          subtitle: (
            <OrganisationUnitsText
              organisationUnitIds={roles[id]?.organisationUnits || []}
            />
          ),
        }))
      );
    }

    if (activeSubMenuKey === 'knowledgeAreas') {
      if (Array.isArray(loadedOptionsByFilter.knowledgeAreas)) {
        return loadedOptionsByFilter.knowledgeAreas;
      }
      const loadedAreaData = loadedOptionsByFilter.knowledgeAreas;
      const areas =
        Object.keys(loadedAreaData?.areas || {}).length > 0
          ? loadedAreaData.areas
          : catalogAreas;
      const categories =
        Object.keys(loadedAreaData?.categories || {}).length > 0
          ? loadedAreaData.categories
          : catalogCategories;
      return sortItemsByName(
        Object.keys(areas).map((id) => ({
          id,
          name: areas[id]?.name || id,
          selected: !filters.knowledgeAreas.deselected.includes(id),
          subtitle: categories[areas[id]?.category] || null,
        }))
      );
    }

    if (activeSubMenuKey === 'knowledgeElements') {
      if (Array.isArray(loadedOptionsByFilter.knowledgeElements)) {
        return loadedOptionsByFilter.knowledgeElements;
      }
      const loadedElementData = loadedOptionsByFilter.knowledgeElements;
      const elements =
        Object.keys(loadedElementData?.elements || {}).length > 0
          ? loadedElementData.elements
          : catalogElements;
      const areas =
        Object.keys(loadedElementData?.areas || {}).length > 0
          ? loadedElementData.areas
          : catalogAreas;
      return sortItemsByName(
        Object.keys(elements).map((id) => ({
          id,
          name: elements[id]?.name || id,
          selected: !filters.knowledgeElements.deselected.includes(id),
          subtitle: areas[elements[id]?.module]?.name || null,
        }))
      );
    }

    if (activeSubMenuKey === 'persons') {
      if (Array.isArray(loadedOptionsByFilter.persons)) {
        return loadedOptionsByFilter.persons;
      }
      const persons =
        Object.keys(loadedOptionsByFilter.persons || {}).length > 0
          ? loadedOptionsByFilter.persons
          : catalogPersons;
      return sortItemsByName(
        Object.keys(persons).map((id) => ({
          id,
          name: persons[id]?.name || id,
          searchString: `${persons[id]?.name || ''}${
            persons[id]?.employeeNumber || ''
          }${persons[id]?.initials || ''}`,
          selected: !filters.persons.deselected.includes(id),
          subtitle: getPersonLikeSubtitle(persons[id]),
          tooltip: getPersonLikeTooltip(persons[id]),
        }))
      );
    }

    if (activeSubMenuKey === 'trainers') {
      if (Array.isArray(loadedOptionsByFilter.trainers)) {
        return sortItemsByName(loadedOptionsByFilter.trainers);
      }
      const trainerCatalog = {
        ...groups,
        ...wildcardPersons,
        ...(Object.keys(loadedOptionsByFilter.trainers || {}).length > 0
          ? loadedOptionsByFilter.trainers
          : catalogPersons),
      };
      return sortItemsByName(
        Object.keys(trainerCatalog).map((id) => ({
          id,
          name: trainerCatalog[id]?.name || id,
          searchString: `${trainerCatalog[id]?.name || ''}${
            trainerCatalog[id]?.employeeNumber || ''
          }${trainerCatalog[id]?.initials || ''}`,
          selected: !filters.trainers.deselected.includes(id),
          subtitle: getPersonLikeSubtitle(trainerCatalog[id]),
          tooltip: getPersonLikeTooltip(trainerCatalog[id]),
        }))
      );
    }

    if (activeSubMenuKey === 'mentors') {
      if (Array.isArray(loadedOptionsByFilter.mentors)) {
        return sortItemsByName(loadedOptionsByFilter.mentors);
      }
      const mentorCatalog = {
        ...groups,
        ...wildcardPersons,
        ...(Object.keys(loadedOptionsByFilter.mentors || {}).length > 0
          ? loadedOptionsByFilter.mentors
          : catalogPersons),
      };
      return sortItemsByName(
        Object.keys(mentorCatalog).map((id) => ({
          id,
          name: mentorCatalog[id]?.name || id,
          searchString: `${mentorCatalog[id]?.name || ''}${
            mentorCatalog[id]?.employeeNumber || ''
          }${mentorCatalog[id]?.initials || ''}`,
          selected: !filters.mentors.deselected.includes(id),
          subtitle: getPersonLikeSubtitle(mentorCatalog[id]),
          tooltip: getPersonLikeTooltip(mentorCatalog[id]),
        }))
      );
    }

    return submenuItems[activeSubMenuKey] || [];
  }, [
    activeSubMenuKey,
    catalogAreas,
    catalogCategories,
    catalogElements,
    catalogOrganisationUnits,
    catalogPersons,
    catalogRoles,
    filters.knowledgeAreas.deselected,
    filters.knowledgeElements.deselected,
    filters.mentors.deselected,
    filters.persons.deselected,
    filters.roles.deselected,
    filters.trainers.deselected,
    groups,
    loadedOptionsByFilter,
    submenuItems,
    wildcardPersons,
  ]);

  const activeSubmenuItems =
    activeSubMenuKey === 'trainers'
      ? [
          ...getTrainerDefaultItems(derivedActiveSubmenuItems),
          ...derivedActiveSubmenuItems.filter(
            (item) =>
              ![
                KNOWLEDGE_OWNER_ID,
                EXPERT_ID,
                MENTOR_ID,
                SELF_STUDY_ID,
                DELETED_PERSON_ID,
                DELETED_GROUP_ID,
                CHAMP_ID,
              ].includes(item.id)
          ),
        ]
      : activeSubMenuKey === 'mentors'
      ? [
          ...getMentorDefaultItems(),
          ...derivedActiveSubmenuItems.filter(
            (item) =>
              ![
                EMPTY_ID,
                NONE_ID,
                DELETED_PERSON_ID,
                DELETED_GROUP_ID,
              ].includes(item.id)
          ),
        ]
      : derivedActiveSubmenuItems;

  const filteredSubmenuItems = useMemo(() => {
    if (!filterSearchQuery || filterSearchQuery.trim() === '') {
      return activeSubmenuItems;
    }

    return activeSubmenuItems.filter((item) => {
      return (
        searchString(item.searchString || item.name || '', filterSearchQuery) ||
        (typeof item.subtitle === 'string' &&
          searchString(item.subtitle, filterSearchQuery))
      );
    });
  }, [activeSubmenuItems, filterSearchQuery]);

  const renderSavedFilter = (filter) => (
    <SlideButton
      actions={[
        {
          icon: 'star',
          onClick: () =>
            onSetDefaultFilter(
              filter.id === defaultFilterId ? null : filter.id
            ),
          title:
            filter.id === defaultFilterId
              ? localeLookup('translations.Remove')
              : localeLookup('translations.Default'),
        },
        {
          icon: 'register',
          onClick: () => onOverwriteSavedFilter(filter),
          title: localeLookup('translations.Update'),
        },
        {
          icon: 'pencil',
          onClick: () => onUpdateSavedFilter(filter),
          title: localeLookup('translations.Rename'),
        },
        {
          color: 'red',
          icon: 'trash2',
          onClick: () => onDeleteSavedFilter(filter),
          title: localeLookup('translations.Delete'),
        },
      ]}
      key={filter.id}
      renderButton={() => (
        <SideNavItem
          active={filter.id === activeFilterId}
          element="div"
          noTruncate
          onClick={() => onApplySavedFilter(filter)}
          rightComponent={
            filter.id === defaultFilterId ? (
              <Icon color="blue" kind="star" />
            ) : null
          }
          subtitle={
            filter.resultCount !== undefined
              ? localeLookup('translations.{0} elements', [filter.resultCount])
              : ''
          }
          title={filter.name}
        />
      )}
      showTrigger
    />
  );

  const renderCategoryRightComponent = ({ key, type }) => {
    if (type === 'settings') {
      if (
        key === 'status' &&
        JSON.stringify(filters.status) !==
          JSON.stringify(filterDefaultState.status)
      ) {
        return <Icon color="blue" kind="settings" />;
      }
      if (
        key === 'elementConfiguration' &&
        JSON.stringify(filters.elementConfiguration) !==
          JSON.stringify(filterDefaultState.elementConfiguration)
      ) {
        return <Icon color="blue" kind="settings" />;
      }
      if (
        key === 'otherFilters' &&
        JSON.stringify(filters.otherFilters) !==
          JSON.stringify(filterDefaultState.otherFilters)
      ) {
        return <Icon color="blue" kind="settings" />;
      }
      return null;
    }

    if (type === 'counter') {
      const filterIds = allFilterIds[key] || [];
      const totalCount = filterIds.length;
      const deselectedIds = new Set(filters[key]?.deselected || []);
      const deselectedCount = filterIds.filter((id) =>
        deselectedIds.has(id)
      ).length;
      const selectedCount = Math.max(0, totalCount - deselectedCount);

      if (deselectedCount === 0) {
        return null;
      }

      return (
        <NotificationCounter color="grey" count={selectedCount} size="small" />
      );
    }

    return null;
  };

  const renderSubMenu = () => {
    if (!activeSubMenuKey) {
      return null;
    }

    const updateFilterSection = (section, stateKey) => {
      onFiltersChange({
        ...filters,
        [section]: {
          ...filters[section],
          [stateKey]: !filters[section][stateKey],
        },
      });
    };

    const resetFilterSection = (section) => {
      onFiltersChange({
        ...filters,
        [section]: filterDefaultState[section],
      });
    };

    if (activeSubMenuKey === 'status') {
      return (
        <AnimatedAside
          animate="visible"
          footerComponent={
            <Button
              fullWidth
              kind="shy"
              onClick={handleSubmenuBack}
            >
              {localeLookup('translations.Back')}
            </Button>
          }
          initial="hidden"
          isSublevel
          variants={submenuVariants}
        >
          <ElementStatusStatusFilter
            filterState={filters.status}
            isDefault={
              JSON.stringify(filters.status) ===
              JSON.stringify(filterDefaultState.status)
            }
            onClickReset={() => resetFilterSection('status')}
            onToggleFilter={(stateKey) =>
              updateFilterSection('status', stateKey)
            }
          />
        </AnimatedAside>
      );
    }

    if (activeSubMenuKey === 'elementConfiguration') {
      return (
        <AnimatedAside
          animate="visible"
          footerComponent={
            <Button
              fullWidth
              kind="shy"
              onClick={handleSubmenuBack}
            >
              {localeLookup('translations.Back')}
            </Button>
          }
          initial="hidden"
          isSublevel
          variants={submenuVariants}
        >
          <ElementStatusElementConfigurationFilter
            filterState={filters.elementConfiguration}
            isDefault={
              JSON.stringify(filters.elementConfiguration) ===
              JSON.stringify(filterDefaultState.elementConfiguration)
            }
            onClickReset={() => resetFilterSection('elementConfiguration')}
            onToggleFilter={(stateKey) =>
              updateFilterSection('elementConfiguration', stateKey)
            }
          />
        </AnimatedAside>
      );
    }

    if (activeSubMenuKey === 'otherFilters') {
      return (
        <AnimatedAside
          animate="visible"
          footerComponent={
            <Button
              fullWidth
              kind="shy"
              onClick={handleSubmenuBack}
            >
              {localeLookup('translations.Back')}
            </Button>
          }
          initial="hidden"
          isSublevel
          variants={submenuVariants}
        >
          <ElementStatusOtherFiltersFilter
            filterState={filters.otherFilters}
            isDefault={
              JSON.stringify(filters.otherFilters) ===
              JSON.stringify(filterDefaultState.otherFilters)
            }
            onClickReset={() => resetFilterSection('otherFilters')}
            onToggleFilter={(stateKey) =>
              updateFilterSection('otherFilters', stateKey)
            }
          />
        </AnimatedAside>
      );
    }

    if (activeSubMenuKey === 'organisationUnits') {
      const isLoadingOrganisationUnits =
        isLoadingOptionsByFilter.organisationUnits;
      return (
        <AnimatedAside
          animate="visible"
          footerComponent={
            <Button
              fullWidth
              kind="shy"
              onClick={handleSubmenuBack}
            >
              {localeLookup('translations.Back')}
            </Button>
          }
          initial="hidden"
          isSublevel
          variants={submenuVariants}
        >
          {isLoadingOrganisationUnits ? (
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                flex: 1,
                justifyContent: 'center',
              }}
            >
              <Loader />
            </div>
          ) : (
            <ElementStatusOrganisationUnitFilter
              filterSearchQuery={filterSearchQuery}
              filterState={filters.organisationUnits}
              isLoading={false}
              onFilterSearchChange={(event) =>
                setFilterSearchQuery(event.target.value)
              }
              onToggleFilter={onOrganisationUnitToggle}
              onToggleMultipleFilters={onOrganisationUnitToggleMultiple}
              organisationUnitRootNodes={
                loadedOrganisationUnits.organisationUnitRootNodes ||
                organisationUnitRootNodes
              }
              organisationUnits={
                loadedOrganisationUnits.organisationUnits || organisationUnits
              }
            />
          )}
        </AnimatedAside>
      );
    }

    const isLoadingActiveSubmenu = isLoadingOptionsByFilter[activeSubMenuKey];

    return (
      <AnimatedAside
        animate="visible"
        footerComponent={
          <Button
            fullWidth
            kind="shy"
            onClick={handleSubmenuBack}
          >
            {localeLookup('translations.Back')}
          </Button>
        }
        initial="hidden"
        isSublevel
        noScroll
        variants={submenuVariants}
      >
        <SideNavTitle
          title={
            filterCategories.find(
              (category) => category.key === activeSubMenuKey
            )?.title
          }
        />
        <SideNavFilter
          filterActions={[
            {
              title: localeLookup('translations.Select all'),
              onClick: () => onSubmenuSelectAll(activeSubMenuKey),
            },
            {
              title: localeLookup('translations.Deselect all'),
              onClick: () =>
                onSubmenuDeselectAll(
                  activeSubMenuKey,
                  activeSubmenuItems.map((item) => item.id)
                ),
            },
          ]}
          onChange={(event) => setFilterSearchQuery(event.target.value)}
          placeholder={`${localeLookup('translations.Search')}...`}
          value={filterSearchQuery}
        />
        <SideNavVirtualListWrapper>
          {isLoadingActiveSubmenu ? (
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                flex: 1,
                justifyContent: 'center',
              }}
            >
              <Loader />
            </div>
          ) : (
            <ElementStatusV2FilterList
              items={filteredSubmenuItems}
              onToggle={(id) => onSubmenuItemToggle(activeSubMenuKey, id)}
              selectedIds={filteredSubmenuItems
                .filter((item) => item.selected)
                .map((item) => item.id)}
            />
          )}
        </SideNavVirtualListWrapper>
      </AnimatedAside>
    );
  };

  return (
    <div className="status-table__sidebar-container">
      <Aside
        className="status-table__sidebar"
        footerComponent={
          <Button
            //className="status-table__sidebar-reset-button"
            disabled={!isAnyFiltersActive(filters, { organisationUnitIds: [] })}
            fullWidth
            kind="alert"
            onClick={onResetFilters}
          >
            {localeLookup('translations.Reset filters')}
          </Button>
        }
      >
        <>
          <SideNavTitle
            icon="plus-circle"
            iconColor="green"
            onIconClick={onCreateSavedFilter}
            title={localeLookup('translations.Saved filters')}
          />
          {!hasFilters && (
            <SideNavText>
              {localeLookup('translations.No saved filters')}
            </SideNavText>
          )}
          {Object.values(defaultFilters).map(renderSavedFilter)}
          {Object.values(presetFilters).map(renderSavedFilter)}
          <SideNavListSection title={localeLookup('translations.Filter')}>
            {filterCategories.map((category) => (
              <SideNavItem
                key={category.key}
                onClick={() => setActiveSubMenuKey(category.key)}
                rightComponent={renderCategoryRightComponent(category)}
                title={category.title}
              />
            ))}
          </SideNavListSection>
          {renderSubMenu()}
        </>
      </Aside>
    </div>
  );
}
